import { TESTER_CAMPAIGN_ID, type TesterTier } from "@shared/testerCampaign";

export const TESTER_TASK_EVENT = "novari:tester-task-completed";
export const testerMarker = (id: string) =>
  `tester:${TESTER_CAMPAIGN_ID}:${id}`;
export const TESTER_TASKS = [
  {
    id: "upload",
    title: "Upload your study material",
    detail: "Try a PDF, Word document, photo or text file.",
    path: "/documents",
  },
  {
    id: "reader",
    title: "Read a document",
    detail: "Open your upload and try the reader tools.",
    path: "/documents",
  },
  {
    id: "chat",
    title: "Ask Novari a question",
    detail: "Ask a follow-up and try a question about your document.",
    path: "/chat",
  },
  {
    id: "quiz",
    title: "Finish a quiz and review your answers",
    detail: "Try another attempt and check your results.",
    path: "/quiz-study",
  },
  {
    id: "flashcards",
    title: "Review some flashcards",
    detail: "Rate a card and check that your review is saved.",
    path: "/flashcards",
  },
  {
    id: "notes",
    title: "Create notes or a lesson",
    detail: "Make a study resource, then reopen it.",
    path: "/summaries",
  },
  {
    id: "planner",
    title: "Try the study planner",
    detail: "Add a task or create a plan for your studies.",
    path: "/planner",
  },
  {
    id: "progress",
    title: "Check your progress",
    detail: "Compare your activity, quiz results and streak.",
    path: "/progress",
  },
  {
    id: "feedback",
    title: "Tell us what worked and what did not",
    detail: "Report a bug or share a suggestion, including the steps you took.",
    path: "/feedback",
  },
] as const;
export type TesterTaskId = (typeof TESTER_TASKS)[number]["id"];
export function testerTasksForTier(tier: TesterTier) {
  return TESTER_TASKS.map((task) => ({
    ...task,
    detail:
      task.id === "chat" && tier === "starter"
        ? "Try your Starter allowance. If you reach a limit, check the explanation and reset time."
        : task.id === "notes" && tier !== "starter"
          ? `Try your ${tier === "max" ? "Max" : "Plus"} generation options and reopen what you made.`
          : task.detail,
  }));
}

/** Successful, completed operations only. No payload, text or document IDs are stored. */
export function taskForRequest(
  path: string,
  method: string,
): TesterTaskId | undefined {
  const clean = path.split("?")[0];
  if (method === "GET") {
    if (/^\/api\/documents\/[^/]+\/(text|content|file)$/.test(clean))
      return "reader";
    if (
      /^\/api\/(analytics\/overview|progress\/overview|mastery\/overview)$/.test(
        clean,
      )
    )
      return "progress";
    if (/^\/api\/(summaries|lessons)\/[^/]+$/.test(clean)) return "notes";
    return;
  }
  if (method !== "POST") return;
  if (clean === "/api/documents/upload") return "upload";
  if (/^\/api\/chat\/sessions\/[^/]+\/messages$/.test(clean)) return "chat";
  if (
    /^\/api\/(quizzes\/v2\/session\/[^/]+\/complete|papers\/attempts\/[^/]+\/complete)$/.test(
      clean,
    )
  )
    return "quiz";
  if (/^\/api\/flashcards\/[^/]+\/rate$/.test(clean)) return "flashcards";
  if (/^\/api\/planner\/(events|tasks|plans)$/.test(clean)) return "planner";
  if (clean === "/api/issues" || clean === "/api/feedback") return "feedback";
}
export function recordTesterRequest(
  userId: string | undefined,
  path: string,
  method = "GET",
) {
  const task = taskForRequest(path, method.toUpperCase());
  if (task && userId && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TESTER_TASK_EVENT, { detail: { userId, task } }),
    );
  }
}
